import React, { createContext, useState, useEffect, useContext } from 'react';
import {
  login as apiLogin,
  logout as apiLogout,
  getCurrentUser,
  loginAsDemoManager,
  googleAuth as apiGoogleAuth,
  appleAuth as apiAppleAuth
} from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [restaurants, setRestaurants] = useState([]);

  useEffect(() => {
    // The session JWT is an httpOnly cookie we can't read from JS. We keep a cached
    // user profile in localStorage purely as a "probably logged in" hint + for
    // instant first render; the cookie is the real credential. With no cached user
    // we assume logged out (and skip /me so public pages don't trigger a redirect).
    const savedUser = localStorage.getItem('user');
    if (!savedUser) {
      setLoading(false);
      return;
    }

    try {
      setUser(JSON.parse(savedUser)); // optimistic render
    } catch (error) {
      localStorage.removeItem('user');
      setLoading(false);
      return;
    }

    // Validate the cookie session and refresh user + restaurants. getCurrentUser
    // opts out of the global 401-redirect, so a stale session here just clears
    // local state — ProtectedRoute handles gating protected pages.
    getCurrentUser()
      .then(response => {
        const u = { ...response.data.user, verified: !!response.data.user.verified };
        setUser(u);
        localStorage.setItem('user', JSON.stringify(u));
        setRestaurants(response.data.restaurants || []);
      })
      .catch(() => {
        localStorage.removeItem('user');
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  // Persist a successful auth response ({ user, restaurants }) into localStorage +
  // state. The JWT itself arrives as an httpOnly cookie, not in the body. Shared by
  // password login, demo login, and OAuth.
  const applySession = (responseData) => {
    const { user: userData, restaurants: userRestaurants } = responseData;
    const u = { ...userData, verified: !!userData.verified };
    localStorage.setItem('user', JSON.stringify(u));
    setUser(u);
    setRestaurants(userRestaurants || []);
  };

  const login = async (email, password) => {
    try {
      const response = await apiLogin(email, password);
      applySession(response.data);
      return { success: true };
    } catch (error) {
      const data = error.response?.data;
      if (error.response?.status === 429) {
        return { success: false, error: 'Too many login attempts. Please wait a moment and try again.' };
      }
      if (error.response?.status === 403 && data?.emailNotVerified) {
        return {
          success: false,
          error: data.error || 'Please verify your email before logging in.',
          emailNotVerified: true,
          email: data.email
        };
      }
      return {
        success: false,
        error: data?.error || error.message || 'Login failed. Please try again.'
      };
    }
  };

  // OAuth sign-in. `credential` is the Google Identity Services ID token.
  const loginWithGoogle = async (credential) => {
    try {
      const response = await apiGoogleAuth(credential);
      applySession(response.data);
      return { success: true };
    } catch (error) {
      const data = error.response?.data;
      if (error.response?.status === 429) {
        return { success: false, error: 'Too many attempts. Please wait a moment and try again.' };
      }
      return {
        success: false,
        error: data?.error || error.message || 'Could not sign in with Google.'
      };
    }
  };

  // OAuth sign-in. `payload` is { id_token, name? } from the Apple JS SDK.
  const loginWithApple = async (payload) => {
    try {
      const response = await apiAppleAuth(payload);
      applySession(response.data);
      return { success: true };
    } catch (error) {
      const data = error.response?.data;
      if (error.response?.status === 429) {
        return { success: false, error: 'Too many attempts. Please wait a moment and try again.' };
      }
      return {
        success: false,
        error: data?.error || error.message || 'Could not sign in with Apple.'
      };
    }
  };

  const loginAsDemo = async () => {
    try {
      const response = await loginAsDemoManager();
      applySession(response.data);
      return { success: true };
    } catch (error) {
      const data = error.response?.data;
      if (error.response?.status === 429) {
        return { success: false, error: data?.error || 'Too many demo sessions started recently. Please wait a moment.' };
      }
      if (error.response?.status === 404) {
        return { success: false, error: 'Demo is not available on this deployment.' };
      }
      return { success: false, error: data?.error || error.message || 'Could not start demo session.' };
    }
  };

  const logout = async () => {
    try {
      await apiLogout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // The auth + CSRF cookies are cleared server-side by /auth/logout; we only
      // drop the cached profile here.
      localStorage.removeItem('user');
      setUser(null);
      setRestaurants([]);
    }
  };

  const isAdmin = () => user?.role === 'admin';
  const isManager = () => user?.role === 'manager';
  const isVerified = () => user?.role === 'admin' || !!user?.verified;
  const isAuthenticated = () => !!user;

  const value = {
    user,
    restaurants,
    loading,
    login,
    loginWithGoogle,
    loginWithApple,
    loginAsDemo,
    logout,
    isAdmin,
    isManager,
    isVerified,
    isAuthenticated,
    setRestaurants
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
